import React, {useState, useEffect, useMemo} from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	BarElement,
	Legend,
	BarController,
	LineController,
	ChartData,
	ChartType,
} from 'chart.js';
import {Chart} from 'react-chartjs-2';
import {useTranslation} from 'react-i18next';
import {StatsElement} from '../../types/MyDynamicsData';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	BarElement,
	BarController,
	LineController,
);

type Props = {
	stat: StatsElement[] | null;
	currentMonthLabel?: string;
	previousMonthLabel?: string;
};

const QualificationChart: React.FC<Props> = ({
	stat,
	currentMonthLabel,
	previousMonthLabel,
}) => {
	const {t} = useTranslation();
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 767.98);
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	// Обрабатываем данные
	const chartData = useMemo(() => {
		const labels: string[] = [];
		const lastMonthData: number[] = [];
		const nowMonthData: number[] = [];

		stat?.forEach((day_stat) => {
			labels.push(day_stat.stat_date.toString());
			lastMonthData.push(day_stat.lastm_points);
			nowMonthData.push(day_stat.nowm_points);
		});

		return {labels, lastMonthData, nowMonthData};
	}, [stat]);

	// Адаптивные размеры шрифтов для мобильных устройств
	const options = useMemo(
		() => {
			const titleFontSize = isMobile ? 11 : 14;
			const ticksFontSize = isMobile ? 10 : 12;
			const legendFontSize = isMobile ? 10 : 12;

			return {
				plugins: {
					datalabels: {
						display: false,
					},
					legend: {
						display: true,
						position: 'top' as const,
						labels: {
							font: {
								size: legendFontSize,
							},
							boxWidth: isMobile ? 12 : 15,
							padding: isMobile ? 8 : 10,
						},
					},
				},
				elements: {
					bar: {
						borderRadius: isMobile ? 3 : 5,
					},
				},
				responsive: true,
				maintainAspectRatio: true,
				scales: {
					x: {
						title: {
							display: true,
							text: t('Дні'),
							font: {
								size: titleFontSize,
								weight: 'bold' as const,
							},
							padding: {
								top: isMobile ? 5 : 10,
							},
						},
						ticks: {
							font: {
								size: ticksFontSize,
							},
							maxRotation: isMobile ? 45 : 0,
							minRotation: isMobile ? 45 : 0,
						},
						beginAtZero: true,
						grid: {
							display: false,
						},
					},
					y: {
						title: {
							display: true,
							text: t('Бали'),
							font: {
								size: titleFontSize,
								weight: 'bold' as const,
							},
							padding: {
								right: isMobile ? 5 : 10,
							},
						},
						ticks: {
							font: {
								size: ticksFontSize,
							},
						},
						beginAtZero: true,
						grid: {
							display: false,
						},
					},
				},
			};
		},
		[isMobile, t],
	);

	// ✅ указываем ChartData<ChartType>, чтобы можно было мешать bar и line
	const data: ChartData<ChartType> = useMemo(
		() => ({
			labels: chartData.labels,
			datasets: [
				{
					type: 'bar' as const,
					label: previousMonthLabel || t('минулий місяць'),
					data: chartData.lastMonthData,
					backgroundColor: '#cfdec3',
					borderWidth: 0,
					order: 2,
				},
				{
					type: 'bar' as const,
					label: currentMonthLabel || t('поточний місяць'),
					data: chartData.nowMonthData,
					backgroundColor: '#4CAF50',
					borderWidth: 0,
					order: 1,
				},
				// 👉 если захочешь линию сверху — просто добавь:
				// {
				//   type: 'line' as const,
				//   label: t('середнє'),
				//   data: chartData.nowMonthData.map(v => v * 0.8), // пример
				//   borderColor: '#ff0000',
				//   borderWidth: 2,
				//   fill: false,
				//   order: 0,
				// },
			],
		}),
		[chartData, previousMonthLabel, currentMonthLabel, t],
	);

	return <Chart type="bar" options={options} data={data} />;
};

export default QualificationChart;
